/**
 * Lap Detection Stage
 *
 * Detects lap boundaries from telemetry using finish line crossings
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { LapDto } from '../../../api/apiTypes'
import { LapBounds } from '../../../domain/lap/LapBounds'
import { GpsPoint } from '../../../domain/telemetry/processed/GpsPoint'
import {
  findFinishLineCrossings,
  lapBoundsBetween,
} from '../../../domain/track-session/finishLineCrossings'

export class LapDetectionStage implements PipelineStage {
  name = 'Lap Detection'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const warnings: string[] = []

    try {
      const { synchronizedTelemetry, track, lapDtos } = context

      if (!synchronizedTelemetry) {
        return {
          success: false,
          error: 'No synchronized telemetry data available',
        }
      }

      // If lap DTOs are provided, use them to determine boundaries
      if (lapDtos && lapDtos.length > 0) {
        const bounds = this.createBoundsFromDtos(
          lapDtos,
          synchronizedTelemetry.gpsPoints
        )
        context.lapBounds = bounds
        return {
          success: true,
          warnings:
            bounds.length === 0
              ? ['No laps could be matched to telemetry']
              : undefined,
        }
      }

      // Otherwise detect laps from finish line
      if (!track.finishLine) {
        // Design decision: treat missing finish line as a soft warning, not a hard
        // failure. Consumers can still display the session as a single lap and allow
        // the user to set a finish line later for proper lap splitting.
        warnings.push(
          'No finish line defined - creating single lap from full telemetry'
        )
        const bounds = this.createSingleLapBounds(
          synchronizedTelemetry.gpsPoints
        )
        context.lapBounds = bounds ? [bounds] : []
        return {
          success: true,
          warnings,
        }
      }

      const crossings = findFinishLineCrossings(
        synchronizedTelemetry.gpsPoints,
        [track.finishLine.startPoint, track.finishLine.endPoint]
      )

      if (crossings.length < 2) {
        warnings.push(
          'Only one finish line crossing detected - creating single lap'
        )
        const bounds = this.createSingleLapBounds(
          synchronizedTelemetry.gpsPoints
        )
        context.lapBounds = bounds ? [bounds] : []
        return {
          success: true,
          warnings,
        }
      }

      context.lapBounds = lapBoundsBetween(crossings)

      return {
        success: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    } catch (error) {
      return {
        success: false,
        error: `Lap detection failed: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }

  /**
   * Create lap bounds from DTO lap information
   */
  private createBoundsFromDtos(
    lapDtos: LapDto[],
    gpsPoints: GpsPoint[]
  ): LapBounds[] {
    const bounds: LapBounds[] = []

    for (const lapDto of lapDtos) {
      // Find GPS points that match lap time boundaries
      const startTime = new Date(lapDto.startTime).getTime()
      const finishTime = new Date(lapDto.finishTime).getTime()

      const startIndex = this.findClosestTimeIndex(gpsPoints, startTime)
      const finishIndex = this.findClosestTimeIndex(gpsPoints, finishTime)

      if (
        startIndex !== null &&
        finishIndex !== null &&
        finishIndex > startIndex
      ) {
        const startPoint = gpsPoints[startIndex]
        const finishPoint = gpsPoints[finishIndex]

        bounds.push({
          startTime: startPoint.timestamp,
          finishTime: finishPoint.timestamp,
          startDistanceMeters: startPoint.distanceMeters,
          finishDistanceMeters: finishPoint.distanceMeters,
        })
      }
    }

    return bounds
  }

  /**
   * Create single lap bounds from all telemetry
   */
  private createSingleLapBounds(gpsPoints: GpsPoint[]): LapBounds | null {
    if (gpsPoints.length === 0) return null

    const firstPoint = gpsPoints[0]
    const lastPoint = gpsPoints[gpsPoints.length - 1]

    return {
      startTime: firstPoint.timestamp,
      finishTime: lastPoint.timestamp,
      startDistanceMeters: firstPoint.distanceMeters,
      finishDistanceMeters: lastPoint.distanceMeters,
    }
  }

  /**
   * Find GPS point index closest to timestamp
   */
  private findClosestTimeIndex(
    points: GpsPoint[],
    targetTimeMs: number
  ): number | null {
    if (points.length === 0) return null

    let closestIndex = 0
    let minDiff = Math.abs(points[0].timestamp.getTime() - targetTimeMs)

    for (let i = 1; i < points.length; i++) {
      const diff = Math.abs(points[i].timestamp.getTime() - targetTimeMs)
      if (diff < minDiff) {
        minDiff = diff
        closestIndex = i
      }
    }

    return closestIndex
  }
}
